/**
 * API Service for Pierag Sales Connect
 *
 * This service provides methods to interact with the backend API
 * for leads, opportunities, customers, and tasks.
 */

// API_BASE_URL — mirrors the resolution logic in pie-sales-crm.tsx so every
// service file resolves the same backend URL regardless of environment.
//   • Production: VITE_API_PRODUCTION_PATH  (e.g. /dev-pieragsalesconnect/api)
//   • Development: VITE_API_BASE_URL or '/api' (relative, works with Vite proxy)
//   • Mixed-content guard: when page is HTTPS but env var points to HTTP, fall
//     back to '/api' (relative) so the browser doesn't block the request.
const _envApiUrl = import.meta.env.MODE === 'production'
  ? import.meta.env.VITE_API_PRODUCTION_PATH || '/dev-pieragsalesconnect/api'
  : import.meta.env.VITE_API_BASE_URL || '/api';

const API_BASE_URL = (typeof window !== 'undefined' && window.location.protocol === 'https:' && _envApiUrl?.startsWith('http://'))
  ? '/api'
  : _envApiUrl;

console.log('API Service initialized with base URL:', API_BASE_URL);

// On 401, never auto-redirect. Verify with /api/auth/whoami first to filter
// out transient blips (Redis hiccup, race during cookie refresh, etc.). Only
// if whoami also confirms the session is dead do we dispatch a global event;
// the app shell catches that event and shows a banner asking the user to
// re-login on their own terms — preserving any unsaved work.
async function notifySessionExpired(): Promise<void> {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (w.__pscSessionExpiredNotified) return;

  let actuallyDead = false;
  try {
    const verify = await fetch(`${API_BASE_URL}/auth/whoami`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (verify.ok) {
      const body = await verify.json().catch(() => ({}));
      // body.authenticated === true means the session is fine; the original
      // 401 was a one-off and we should NOT bother the user.
      actuallyDead = body && body.authenticated === false;
    }
  } catch {
    // whoami itself failed — be conservative and stay quiet, the user is
    // probably offline or the backend is briefly down. Better silent than
    // an erroneous "session expired" banner.
    return;
  }

  if (!actuallyDead) return;
  w.__pscSessionExpiredNotified = true;
  window.dispatchEvent(new CustomEvent('psc:session-expired'));
}

// Generic API request handler
async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit
): Promise<{ success: boolean; data?: T; message?: string; error?: string }> {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    });

    // Auth gone — verify with whoami and (if confirmed) raise a banner.
    // We never auto-redirect; the user keeps their unsaved work intact and
    // chooses when to re-login.
    if (response.status === 401) {
      void notifySessionExpired();
      return { success: false, error: 'Session expired' };
    }

    let result;
    try {
      result = await response.json();
    } catch (parseErr) {
      // Response was not JSON (e.g. HTML error page from reverse proxy)
      const text = await response.text().catch(() => '');
      throw new Error(`API returned non-JSON response (HTTP ${response.status}) from ${url}. ${text.substring(0, 200)}`);
    }

    if (!response.ok) {
      throw new Error(result.message || result.error || `API request failed (HTTP ${response.status})`);
    }

    return result;
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

// =============================================
// LEADS API
// =============================================

export const leadsApi = {
  // Get all leads
  getAll: async () => {
    return apiRequest<any[]>('/leads');
  },

  // Get single lead by ID
  getById: async (id: string) => {
    return apiRequest<any>(`/leads/${id}`);
  },

  // Create new lead
  create: async (lead: any) => {
    return apiRequest<any>('/leads', {
      method: 'POST',
      body: JSON.stringify(lead),
    });
  },

  // Update lead
  update: async (id: string, lead: any) => {
    return apiRequest<any>(`/leads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(lead),
    });
  },

  // Delete lead
  delete: async (id: string) => {
    return apiRequest(`/leads/${id}`, {
      method: 'DELETE',
    });
  },

  // Convert lead to opportunity (legacy — creates opp in DB)
  convertToOpportunity: async (id: string) => {
    return apiRequest<any>(`/leads/convert/${id}`, {
      method: 'POST',
    });
  },

  // Mark lead as converted (status only — opportunity created separately)
  markConverted: async (id: string, opportunityId?: string, engagementHistory?: string) => {
    return apiRequest<any>(`/leads/mark-converted/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ opportunity_id: opportunityId || null, engagement_history: engagementHistory || null }),
    });
  },
};

// =============================================
// OPPORTUNITIES API
// =============================================

export const opportunitiesApi = {
  // Get all opportunities
  getAll: async () => {
    return apiRequest<any[]>('/opportunities');
  },

  // Get single opportunity by ID
  getById: async (id: string) => {
    return apiRequest<any>(`/opportunities/${id}`);
  },

  // Create new opportunity
  create: async (opportunity: any) => {
    return apiRequest<any>('/opportunities', {
      method: 'POST',
      body: JSON.stringify(opportunity),
    });
  },

  // Update opportunity
  update: async (id: string, opportunity: any) => {
    return apiRequest<any>(`/opportunities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(opportunity),
    });
  },

  // Delete opportunity
  delete: async (id: string) => {
    return apiRequest(`/opportunities/${id}`, {
      method: 'DELETE',
    });
  },
};

// =============================================
// CUSTOMERS API
// =============================================

export const customersApi = {
  // Get all customers
  getAll: async () => {
    return apiRequest<any[]>('/customers');
  },

  // Get single customer by ID
  getById: async (id: string) => {
    return apiRequest<any>(`/customers/${id}`);
  },

  // Create new customer
  create: async (customer: any) => {
    return apiRequest<any>('/customers', {
      method: 'POST',
      body: JSON.stringify(customer),
    });
  },

  // Update customer
  update: async (id: string, customer: any) => {
    return apiRequest<any>(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customer),
    });
  },

  // Delete customer
  delete: async (id: string) => {
    return apiRequest(`/customers/${id}`, {
      method: 'DELETE',
    });
  },
};

// =============================================
// TASKS API
// =============================================

export const tasksApi = {
  // Get all tasks
  getAll: async () => {
    return apiRequest<any[]>('/tasks');
  },

  // Get single task by ID
  getById: async (id: string) => {
    return apiRequest<any>(`/tasks/${id}`);
  },

  // Create new task
  create: async (task: any) => {
    return apiRequest<any>('/tasks', {
      method: 'POST',
      body: JSON.stringify(task),
    });
  },

  // Update task
  update: async (id: string, task: any) => {
    return apiRequest<any>(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(task),
    });
  },

  // Delete task
  delete: async (id: string) => {
    return apiRequest(`/tasks/${id}`, {
      method: 'DELETE',
    });
  },
};

// =============================================
// MASTER DATA API - Generic CRUD for all master data tables
// =============================================

/**
 * Generic master data API factory
 * Creates CRUD operations for any master data endpoint
 */
const createMasterDataApi = (endpoint: string) => ({
  getAll: async () => apiRequest<any[]>(`/master/${endpoint}`),
  create: async (data: any) =>
    apiRequest<any>(`/master/${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: async (id: number, data: any) =>
    apiRequest<any>(`/master/${endpoint}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: async (id: number) =>
    apiRequest(`/master/${endpoint}/${id}`, {
      method: 'DELETE',
    }),
});

// Sales Stages API
export const salesStagesApi = createMasterDataApi('sales-stages');

// Industries API
export const industriesApi = createMasterDataApi('industries');

// Service Lines API
export const serviceLinesApi = createMasterDataApi('service-lines');

// Business Units API
export const businessUnitsApi = createMasterDataApi('business-units');

// Geographies API
export const geographiesApi = createMasterDataApi('geographies');

// Entities API
export const entitiesApi = createMasterDataApi('entities');

// Countries API
export const countriesApi = createMasterDataApi('countries');

// Service Offerings API
export const serviceOfferingsApi = createMasterDataApi('service-offerings');

// Lead Sources API
export const leadSourcesApi = createMasterDataApi('lead-sources');

// =============================================
// USERS API
// =============================================

export const usersApi = {
  // Get all users
  getAll: async () => {
    return apiRequest<any[]>('/master/users');
  },

  // Get single user by ID
  getById: async (id: string) => {
    return apiRequest<any>(`/master/users/${id}`);
  },

  // Create new user
  create: async (user: any) => {
    return apiRequest<any>('/master/users', {
      method: 'POST',
      body: JSON.stringify(user),
    });
  },

  // Update user
  update: async (id: string, user: any) => {
    return apiRequest<any>(`/master/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(user),
    });
  },

  // Delete user
  delete: async (id: string) => {
    return apiRequest(`/master/users/${id}`, {
      method: 'DELETE',
    });
  },
};

// =============================================
// HEALTH CHECK
// =============================================

export const healthCheck = async () => {
  return apiRequest('/health');
};

// Export all APIs
export default {
  leads: leadsApi,
  opportunities: opportunitiesApi,
  customers: customersApi,
  tasks: tasksApi,
  users: usersApi,
  salesStages: salesStagesApi,
  industries: industriesApi,
  serviceLines: serviceLinesApi,
  businessUnits: businessUnitsApi,
  geographies: geographiesApi,
  entities: entitiesApi,
  countries: countriesApi,
  serviceOfferings: serviceOfferingsApi,
  leadSources: leadSourcesApi,
  healthCheck,
};
